import React, { useEffect } from "react";
import '../eyeTest.css';
import { useRecoilState } from 'recoil';
import { Label } from '@nooz-optics/nooz-components-lib';
import { eyeTestState } from '../../../../utils/recoil/eyeTest';
import { formatContentfulComponentProps } from '../../../../utils/globals';
import { Button } from '../../../UI/Elements';

// If you want to change the Q & A on Contentful, use the Json "MetaData" from "EyeTest Form"

export const EyeTestForm = ({ triggerEvent, scrollUp, formStep }) => {
  const {
    backLabel,
    eyeTestBlurButton,
    eyeTestHelperLabel,
    eyeTestNextQuestionButton,
    form,
    label,
    readProperlyLabel,
  } = formatContentfulComponentProps(formStep);
  const [eyeTest, setEyeTest] = useRecoilState(eyeTestState);
  const { currentStep, formValue } = eyeTest;

  // Dynamic text sizing values for Eye Test text
  const textSizing = [
    { 1: '20px', 2: '30.7px', lineHeight: 1.35 },
    { 1: '17px', 2: '28px', lineHeight: 1.3 },
    { 1: '15px', 2: '24.3px', lineHeight: 1.28 },
    { 1: '11.8px', 2: '17.3px', lineHeight: 1.25 },
    { 1: '9.2px', 2: '13.3px', lineHeight: 1.2 },
  ];

  const devicePixelRatio = () => {
    if (window.devicePixelRatio === 1) {
      return textSizing[currentStep][1];
    } else if (window.devicePixelRatio === 2) {
      return textSizing[currentStep][2];
    }
    return textSizing[currentStep][1];
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (formValue === 'correct' && currentStep === form.inputs.length - 1) {
      setEyeTest({
        ...eyeTest,
        fullSuccess: true,
        formValue: '',
        isCompleted: true,
      });
      scrollUp();
    } else if (
      formValue === 'correct' &&
      currentStep < form.inputs.length - 1
    ) {
      setEyeTest({
        ...eyeTest,
        formValue: '',
        fullSuccess: false,
        currentStep: currentStep + 1,
      });
      scrollUp();
    } else if (formValue === '') {
      setEyeTest({
        ...eyeTest,
        fullSuccess: false,
      });
    } else {
      setEyeTest({
        ...eyeTest,
        fullSuccess: false,
        formValue: '',
        isCompleted: true,
      });
      scrollUp();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setEyeTest({
        ...eyeTest,
        formValue: '',
        currentStep: currentStep - 1,
      });
      scrollUp();
    } else {
      setEyeTest({
        ...eyeTest,
        isInit: true,
        formValue: '',
        currentStep: 0,
      });
      scrollUp();
    }
  };

  const handleBlur = () => {
    setEyeTest({
      ...eyeTest,
      formValue: '',
      isCompleted: true,
    });
    scrollUp();
  };

  const returnFormControls = () =>
    form.inputs[currentStep].metaData.answers.map((answer) => (
      <div className='flex items-center'>
        <input
          type='radio'
          id={answer.type}
          name='answer'
          value={answer.type}
          className='my-1 mr-4 w-6 h-6'
          onChange={(event) => {
            setEyeTest({
              ...eyeTest,
              formValue: event.target.value,
            });
          }}
        />
        <label htmlFor={answer.type}>
          <Label text={answer.answer} />
        </label>
      </div>
    ));

  const returnFormSteps = () =>
    form.inputs.map((d, index) => {
      const count = `${index + 1}/${form.inputs.length}`;
      if (index === currentStep) {
        return (
          <div className='pt-10 lg:pt-0'>
            <div className='flex items-center'>
              <div className='cursor-default mr-5'>
                <Button
                  size='base'
                  label={{
                    text: `+${d.metaData.diopter}`,
                  }}
                  type='white'
                  onClick={handleBack}
                />
              </div>
              <div>
                <Label
                  weight='bold'
                  text={`${count} ${d.metaData.level}`}
                />
                <Label text={eyeTestHelperLabel.text} />
              </div>
            </div>
            <div className='border-b mt-5 mb-10' />
            <Label
              weight='bold'
              size='2xl'
              text={form.inputs[currentStep].metaData.question}
            />
            <form onSubmit={(event) => handleSubmit(event)}>
              <div className='ml-8 py-7'>{returnFormControls()}</div>
              <div className='pb-5'>
                <Button
                  htmlType='submit'
                  type='contained'
                  color={{ classname: 'bluelight' }}
                  label={{ text: eyeTestNextQuestionButton.label?.text }}
                />
              </div>
            </form>
          </div>
        );
      }
      return '';
    });

  const returnButtons = () => (
    <div>
      <Label
        weight='bold'
        size='2xl'
        text={readProperlyLabel.text}
      />
      <div className='flex flex-col lg:flex lg:flex-row justify-between pt-5 pb-5 lg:pb-0'>
        <div className='py-5'>
          <Button
            type='outlined'
            label={{ text: eyeTestBlurButton.label?.text }}
            onClick={handleBlur}
          />
        </div>
        <div className='py-5'>
          <Button
            onClick={handleBack}
            type='outlined'
            label={{ text: backLabel.text }}
          />
        </div>
      </div>
    </div>
  );

  const returnTextContent = () => (
    <p
      className='font-gilmer font-bold text-black'
      style={{
        fontSize: devicePixelRatio(),
        lineHeight: textSizing[currentStep].lineHeight,
      }}>
      {form.inputs[currentStep].metaData.text}
    </p>
  );

  useEffect(() => {
    triggerEvent();
  }, [])

  return (
    <div className='w-full eye-test-container lg:flex lg:flex-row'>
      <div className='lg:absolute right-0 eye-test-bg eye-test-text px-8 flex justify-center items-center lg:w-1/2 lg:px-12'>
        {returnTextContent()}
      </div>
      <div className='w-full h-full flex flex-col justify-center pt-2 lg:pr-10 lg:w-1/2 lg:pt-0'>
        {returnFormSteps()}
        <div className='flex justify-center py-10'>
          <span className='font-gilmer font-bold line-through-word'>
            {`   ${label?.text}   `}
          </span>
        </div>
        {returnButtons()}
      </div>
    </div>
  );
};
